import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Activity, AlertTriangle, BarChart3, Bell, CalendarDays,
  CalendarRange, CheckCircle2, CheckSquare, ChevronRight, Circle, ClipboardList,
  Dumbbell, ExternalLink, HeartPulse, LayoutDashboard, ListChecks, LogOut, Mail, Mic,
  MessageSquare, PackageCheck, RefreshCw, Settings, ShoppingCart, Stethoscope,
  ShieldCheck, Trophy, Users, Utensils, Wrench,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { getServiceCategoryIcon } from "../lib/serviceCategoryIcons";
import { formatDuration } from "../lib/media";
import { careScoreBand, CARE_SCORE_TONES } from "../config/careDashboard";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import AccountProfileSettings from "../components/AccountProfileSettings";
import LeaveSelfService from "../components/LeaveSelfService";
import CareRequestsPanel from "../components/CareRequestsPanel";
import CareServicesWorkspace from "../components/CareServicesWorkspace";
import CareInventoryWorkspace from "../components/CareInventoryWorkspace";
import CareOrdersWorkspace from "../components/CareOrdersWorkspace";
import CareTasksWorkspace from "../components/CareTasksWorkspace";
import CareReportsWorkspace from "../components/CareReportsWorkspace";
import CareNotificationInbox from "../components/CareNotificationInbox";
import CareEmailWorkspace from "../components/CareEmailWorkspace";
import PlayerNotificationBell from "../components/PlayerNotificationBell";
import { PlayerActionImage, PlayerPortrait } from "../components/PlayerMedia";
import { dayKey, fmtDateTime } from "../lib/time";
import { getTrainingUnitIcon, trainingUnitDisplayTitle, trainingUnitLabel } from "../lib/trainingPlanUnits";
import { CARE_MAIL_WINDOW_NAME, openCareMailWindow } from "../lib/careMailWindow";
import ThemeLevelSlider from "../components/ThemeLevelSlider";
import WeeklyRoutineWorkspace from "../components/WeeklyRoutineWorkspace";
import { applyTheme, normalizeTheme } from "../lib/theme";
import "./CareCalendarResponsive.css";

const NAV_ITEMS = [
  ["dashboard", "dashboard", LayoutDashboard],
  ["calendar", "calendar", CalendarDays],
  ["players", "players", Users],
  ["nutrition", "nutrition", Utensils],
  ["inventory", "inventory", PackageCheck],
  ["orders", "orders", ShoppingCart],
  ["tasks", "tasks", CheckSquare],
  ["requests", "requests", ClipboardList],
  ["services", "services", Wrench],
  ["reports", "reports", BarChart3],
  ["settings", "settings", Settings],
];

const VALID_VIEWS = new Set([...NAV_ITEMS.map(([key]) => key), "email", "notifications"]);
const TASK_CATEGORIES = new Set(["task", "care_task", "organization"]);

export const careTaskDeepLink = (taskId) => `/care?view=tasks&bucket=all&task=${encodeURIComponent(taskId)}`;

function scheduleDisplayTitle(item, t) {
  if (!["match", "game"].includes((item.category || "").toLowerCase())) {
    return item.training_unit_type ? trainingUnitDisplayTitle(item, t) : item.title;
  }
  const matchType = item.match?.match_type || "league";
  return `${t(`matchType_${matchType}`)}${item.match?.opponent ? ` · ${item.match.opponent}` : ""}`;
}

function Avatar({ player, size = 48 }) {
  return <PlayerPortrait player={player} size={size} />;
}

function ScoreAvatar({ player, score, tone, size = 98 }) {
  const progress = score == null ? 0 : Math.max(0, Math.min(100, Number(score) || 0));
  const degrees = progress * 3.6;
  const portraitHeight = Math.round(size * 1.28);
  return <span className="rounded-2xl shrink-0 grid place-items-center" data-testid="player-score-ring"
    data-score-progress={Math.round(progress)} role="img" aria-label={`${player.name}: ${Math.round(progress)}%`}
    style={{
      width: size, height: portraitHeight, padding: 4,
      background: `conic-gradient(from 0deg, ${tone.accent} 0deg ${degrees}deg, var(--pc-ring-track) ${degrees}deg 360deg)`,
      boxShadow: `0 0 24px ${tone.soft}, 0 12px 30px rgba(0,0,0,.22)`,
    }}>
    <span className="rounded-[13px] overflow-hidden grid place-items-center" style={{
      width: "100%", height: "100%", padding: 3,
      background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)",
    }}><PlayerPortrait player={player} size={size - 14} height={portraitHeight - 14} shapeClassName="rounded-[10px]" /></span>
  </span>;
}

const localDateOrdinal = (value, timezone) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric", month: "2-digit", day: "2-digit", timeZone: timezone || "UTC",
  }).formatToParts(date).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
  return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day));
};

const daysUntilPlayerDate = (from, target, timezone) => {
  const fromDay = localDateOrdinal(from, timezone);
  const targetDay = localDateOrdinal(target, timezone);
  return fromDay == null || targetDay == null ? null : Math.round((targetDay - fromDay) / 86400000);
};

export function CareSidebar({ view, onNavigate, theme, setTheme, user, logout, onOpenMail = openCareMailWindow }) {
  const { t } = useI18n();
  const nav = useNavigate();
  return <aside className="pc-care-sidebar w-[248px] shrink-0 border-r flex flex-col"
    style={{ background: "linear-gradient(180deg, var(--pc-charcoal), var(--pc-card-deep))", borderColor: "var(--pc-border)" }} data-testid="care-sidebar">
    <div className="pc-care-sidebar__header px-5 py-5 border-b" style={{ borderColor: "var(--pc-border)" }} data-testid="care-sidebar-header">
      <p className="text-xs font-semibold uppercase tracking-[.2em]" style={{ color: "var(--pc-cyan)" }}>Performance Care</p>
      <p className="font-display font-semibold mt-2">{t("operationsCenter")}</p>
      <p className="text-xs mt-1 truncate" style={{ color: "var(--pc-muted)" }}>{user?.name}</p>
      {user?.role === "admin" && <div className="mt-3 rounded-xl px-3 py-2 text-xs font-semibold flex items-center gap-2" style={{ background: "var(--pc-cyan-dim)", color: "var(--pc-cyan)", border: "1px solid var(--pc-cyan-border)" }} data-testid="admin-care-mode"><ShieldCheck size={14} /> {t("operatingAsAdmin")}</div>}
    </div>
    <nav className="pc-care-sidebar__nav p-3 space-y-1 flex-1" aria-label={t("careNavigation")} data-testid="care-sidebar-nav">
      {NAV_ITEMS.map(([key, label, Icon]) => <button key={key} type="button"
        data-testid={`care-nav-${key}`} onClick={() => onNavigate(key)}
        className="pc-care-sidebar__nav-item w-full rounded-xl px-3 py-2.5 flex items-center gap-3 text-sm font-semibold text-left"
        style={{
          background: view === key ? "var(--pc-cyan-dim)" : "transparent",
          color: view === key ? "var(--pc-cyan)" : "var(--pc-muted)",
          border: `1px solid ${view === key ? "rgba(0,229,255,.24)" : "transparent"}`,
        }} aria-current={view === key ? "page" : undefined}>
        <Icon size={17} /> {t(label)}
      </button>)}
    </nav>
    <div className="pc-care-sidebar__utilities p-3 border-t space-y-2" style={{ borderColor: "var(--pc-border)" }} data-testid="care-sidebar-utilities">
      {user?.role === "admin" && <button type="button" onClick={() => nav("/admin")} className="pc-btn pc-btn-ghost w-full flex items-center justify-center gap-2" style={{ padding: "9px 12px", fontSize: 13 }}><ShieldCheck size={15} /> {t("backToAdmin")}</button>}
      <a href="/care/mail" target={CARE_MAIL_WINDOW_NAME} data-testid="care-mail-launcher" onClick={() => onOpenMail()}
        className="pc-care-mail-launcher" aria-current={view === "email" ? "page" : undefined}
        aria-label={t("mailOpenWindow")}>
        <span className="pc-care-mail-launcher__icon"><Mail size={18} /></span>
        <span>{t("email")}</span><ExternalLink size={14} className="ml-auto" />
      </a>
      <ThemeLevelSlider value={theme} onChange={setTheme} testId="theme-slider" />
      <div className="flex gap-2"><LanguageSwitcher /><button data-testid="logout-btn" type="button"
        onClick={async () => { await logout(); nav("/login"); }} className="pc-btn pc-btn-ghost flex-1 flex items-center justify-center gap-2"
        style={{ padding: "9px 12px", fontSize: 13 }}><LogOut size={15} /> {t("logout")}</button></div>
    </div>
  </aside>;
}

export function CareScopeToggle({ scope, onChange }) {
  const { t } = useI18n();
  return <div className="pc-care-scope-control" data-testid="care-scope-control">
    <div className="pc-care-scope-control__segments" role="group" aria-label={t("carePlayerScope")}>
      {["mine", "all"].map((value) => <button key={value} type="button" onClick={() => onChange(value)}
        className={`pc-care-scope-control__segment ${scope === value ? "pc-care-scope-control__segment--active" : ""}`}
        aria-pressed={scope === value} data-testid={`care-scope-${value}`}>
        {t(value === "mine" ? "myPlayers" : "allPlayers")}
      </button>)}
    </div>
    {scope === "all" && <span className="hidden 2xl:inline text-[10px] uppercase tracking-[.12em]" style={{ color: "var(--pc-cyan)" }} data-testid="admin-all-player-scope">{t("adminAllPlayersView")}</span>}
  </div>;
}

export function CarePlayerCard({ row, onOpen }) {
  const { t, lang } = useI18n();
  const player = row.player;
  const summary = row.yesterday_daily_summary;
  const metric = row.primary_metric || { type: "gamepower", score: row.gamepower?.score || 0 };
  const injuryMode = metric.type === "recovery";
  const nextMatch = row.next_match;
  const expectedReturn = row.status?.injury_case?.expected_return;
  const returnDays = daysUntilPlayerDate(row.status?.calculated_at, expectedReturn, player.timezone);
  const score = summary?.performance_score;
  const band = careScoreBand(score);
  const tone = CARE_SCORE_TONES[band];
  return <button type="button" onClick={() => onOpen(player.id)}
    className="pc-card group px-4 py-3 text-left h-[150px] w-full overflow-hidden transition-[transform,border-color,box-shadow] hover:-translate-y-0.5"
    style={{
      borderColor: `color-mix(in srgb, ${tone.accent} 46%, var(--pc-border))`,
      background: `linear-gradient(118deg, ${tone.soft}, var(--pc-charcoal) 48%, var(--pc-card-deep))`,
      boxShadow: `inset 0 0 34px ${tone.soft}, 0 16px 36px rgba(0,0,0,.18)`,
    }}
    data-testid={`cm-player-card-${player.id}`} data-score-band={band}
    aria-label={`${player.name} – ${t("openPlayerRecord")}`}>
    <div className="grid grid-cols-[90px_minmax(0,1fr)_110px] gap-3 items-center h-full">
      <ScoreAvatar player={player} score={metric.score} tone={tone} size={90} />
      <div className="min-w-0">
        <p className="text-[15px] leading-[1.15] min-h-[35px] font-display font-semibold break-words line-clamp-2" data-testid="player-card-name">{player.name}</p>
        <div className="mt-2" data-testid="player-card-yesterday-score">
          <p className="text-[10px] uppercase tracking-[.14em]" style={{ color: "var(--pc-muted)" }}>{t("yesterdayScore")}</p>
          <p className="text-[30px] leading-none font-display font-bold mt-1" style={{ color: tone.accent }}>
            {score == null ? "–" : `${Math.round(score)}%`}
          </p>
        </div>
        <div className="flex items-baseline gap-1.5 mt-1.5" data-testid="player-card-primary-metric">
          <p className="text-[9px] uppercase tracking-[.12em]" style={{ color: "var(--pc-muted)" }}>{t(metric.type)}</p>
          <p className="text-base leading-none font-display font-semibold" style={{ color: "var(--pc-cyan)" }}>{Math.round(metric.score || 0)}%</p>
        </div>
      </div>
      <div className="self-stretch min-w-0 border-l pl-3 flex flex-col justify-center" style={{ borderColor: "var(--pc-border)" }} data-testid="player-card-next-match">
        {injuryMode ? <>
          <span className="flex items-start gap-1 text-[9px] leading-tight uppercase tracking-[.1em]" style={{ color: "var(--pc-muted)" }}><HeartPulse size={11} className="shrink-0 mt-px" /><span>{returnDays != null && returnDays <= 0 ? t("returnReview") : t("expectedReturnEstimate")}</span></span>
          {expectedReturn ? <span className="text-xs leading-snug font-semibold mt-2 line-clamp-2">{returnDays != null && returnDays <= 0 ? t("returnSince").replace("{date}", compactReturnDate(expectedReturn, player.timezone, lang)) : compactReturnDate(expectedReturn, player.timezone, lang)}</span>
            : <span className="text-xs font-semibold mt-1.5" style={{ color: "var(--pc-muted)" }}>{t("returnOpen")}</span>}
        </> : <>
          <span className="flex items-center gap-1 text-[9px] uppercase tracking-[.12em]" style={{ color: "var(--pc-muted)" }}><Trophy size={11} />{t("nextMatch")}</span>
          {nextMatch ? <><span className="text-xs font-semibold mt-1.5 line-clamp-2">{nextMatch.match?.opponent || scheduleDisplayTitle(nextMatch, t)}</span>
            <span className="text-[10px] leading-snug mt-1" style={{ color: "var(--pc-muted)" }}>{compactMatchDate(nextMatch.start_at, player.timezone, lang)}</span></>
            : <span className="text-sm font-semibold mt-1.5" style={{ color: "var(--pc-muted)" }}>–</span>}
        </>}
      </div>
    </div>
  </button>;
}

const compactCareStartDate = (value, lang) => value ? new Intl.DateTimeFormat(
  lang === "de" ? "de-DE" : "en-GB", { day: "2-digit", month: "2-digit", year: "numeric" },
).format(new Date(`${value}T12:00:00`)) : "–";

export function CarePreparationPlayerCard({ row, onOpen }) {
  const { t, lang } = useI18n();
  const player = row.player;
  const onboarding = row.onboarding || {};
  const ready = onboarding.preparation_status === "ready_for_activation";
  return <button type="button" onClick={() => onOpen(player.id)}
    className="pc-card group px-4 py-3 text-left h-[150px] w-full overflow-hidden transition-[transform,border-color,box-shadow] hover:-translate-y-0.5"
    style={{
      borderColor: ready ? "var(--pc-score-good)" : "var(--pc-cyan-border)",
      background: "linear-gradient(118deg, var(--pc-cyan-dim), var(--pc-charcoal) 52%, var(--pc-card-deep))",
      boxShadow: "inset 0 0 30px var(--pc-cyan-dim), 0 16px 36px rgba(0,0,0,.16)",
    }} data-testid={`cm-preparation-player-card-${player.id}`}
    aria-label={`${player.name} – ${t("openOnboardingPreparation")}`}>
    <div className="grid grid-cols-[58px_minmax(0,1fr)_minmax(112px,auto)] gap-3 items-center h-full">
      <Avatar player={player} size={58} />
      <span className="min-w-0">
        <span className="block text-[15px] leading-tight font-display font-semibold whitespace-normal break-words">{player.name}</span>
        <span className="block text-[10px] uppercase tracking-[.12em] mt-2" style={{ color: "var(--pc-muted)" }}>{t("careStart")}: <strong className="text-sm normal-case tracking-normal" style={{ color: "var(--pc-text)" }}>{compactCareStartDate(onboarding.care_start_date, lang)}</strong></span>
      </span>
      <span className="min-w-0 flex flex-col items-end justify-center gap-2">
        <span className="inline-flex rounded-full px-2 py-1 text-[9px] text-right font-semibold uppercase tracking-[.1em]" style={{ color: ready ? "var(--pc-score-good)" : "var(--pc-cyan)", background: "var(--pc-card-inset)", border: "1px solid var(--pc-border)" }} data-testid="preparation-status-badge">{t(ready ? "readyForActivation" : "preparationStatus")}</span>
        <span className="text-[11px] font-semibold flex items-center gap-1" style={{ color: "var(--pc-cyan)" }}>{t("openPreparation")}<ChevronRight size={13} /></span>
      </span>
    </div>
  </button>;
}

function PlayerGroups({ rows, onOpen, onOpenPreparation, emptyText }) {
  const { t } = useI18n();
  return <section data-testid="dashboard-player-groups">
    {rows.length > 0 && <><h2 className="text-sm font-display font-semibold mb-2.5">{t("assignedPlayers")}</h2>
      <div className="grid gap-2.5 items-start justify-start" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(350px, 380px))" }}>
        {rows.map((row) => <div key={row.player.id} data-testid={row.assignment?.relationship === "coverage" ? "coverage-player-group" : "primary-player-group"}>
          {row.onboarding
            ? <CarePreparationPlayerCard row={row} onOpen={onOpenPreparation} />
            : <CarePlayerCard row={row} onOpen={onOpen} />}
          {row.assignment?.relationship === "coverage" && <p className="text-xs mt-2 px-1" style={{ color: "var(--pc-cyan)" }}>{t("coverageFor")} {row.assignment?.primary_name || t("anotherCareManager")}</p>}
        </div>)}
      </div></>}
    {rows.length === 0 && <div className="pc-card p-8 text-center" style={{ color: "var(--pc-muted)" }}>{emptyText || t("noPlayers")}</div>}
  </section>;
}

function MetricButton({ label, value, Icon, onClick, testId }) {
  return <button type="button" onClick={onClick} data-testid={testId}
    className="pc-card px-3.5 py-2.5 text-left flex items-center gap-2.5 min-w-0 min-h-[58px]">
    <span className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--pc-cyan-dim)", color: "var(--pc-cyan)" }}><Icon size={16} /></span>
    <span className="min-w-0"><span className="block text-xl leading-none font-display font-bold">{value || 0}</span><span className="block text-xs truncate mt-1" style={{ color: "var(--pc-muted)" }}>{label}</span></span>
    <ChevronRight size={15} className="ml-auto shrink-0" color="var(--pc-muted)" />
  </button>;
}

const localizeSystemCopy = (value, t) => {
  if (!value) return "";
  const normalized = value.trim().replace(/[.]$/, "").toLowerCase();
  if (["delivery address is missing", "delivery address missing"].includes(normalized)) return t("addressMissing");
  const inventoryTitle = value.match(/^Inventory could not be reconciled:\s*(.+)$/i);
  if (inventoryTitle) return `${t("inventoryReconciliationFailed")}: ${inventoryTitle[1]}`;
  const insufficientInventory = value.match(/^409:\s*Insufficient inventory for\s+(.+)$/i);
  if (insufficientInventory) return `${t("insufficientInventory")}: ${insufficientInventory[1]}`;
  return value;
};

export const attentionCopy = (attention, t) => {
  let title = localizeSystemCopy(attention.title, t);
  let body = localizeSystemCopy(attention.body, t);
  if (attention.kind === "weekly_meal_plan_incomplete") {
    const { week_start: start, week_end: end } = attention.metadata || {};
    const range = start && end ? `${start.split("-").reverse().slice(0, 2).join(".")}.–${end.split("-").reverse().slice(0, 2).join(".")}.` : "";
    title = [t("weeklyPlan"), range, t("incomplete")].filter(Boolean).join(" ");
    const blockers = attention.metadata?.blockers || [];
    body = blockers.length ? blockers.map((row) => {
      if (row.code === "prepared_meal_count_shortfall") return `${t("preparedMealsPlanned")}: ${row.planned_count} / ${row.required_count} (${row.missing_count} ${t("missing")})`;
      return localizeSystemCopy(row.message, t);
    }).join(" · ") : body;
  } else if (attention.kind === "weekly_meal_delivery_address_missing") {
    title = t("addressMissing");
  } else if (/^wochenplan unvollständig\s*[–-]/i.test(title)) {
    const detail = title.split(/\s*[–-]\s*/).slice(1).join(" – ").replace(/[.]$/, "");
    title = t("weeklyPlanIncomplete");
    body = [detail || t("orderCannotComplete"), body].filter(Boolean).join(" · ");
  } else if (/^weekly plan incomplete\s*[–-]/i.test(title)) {
    title = t("weeklyPlanIncomplete");
    body = [t("orderCannotComplete"), body].filter(Boolean).join(" · ");
  }
  return { title, body };
};

function AttentionSection({ attentions, urgentIds, focus, onClearFocus, onOpen, onSnooze, onActivate, onOpenTask, onContact, onOpenPlayer, playerById }) {
  const { t } = useI18n();
  const urgentSet = useMemo(() => new Set(urgentIds || []), [urgentIds]);
  const visible = focus === "urgent"
    ? attentions.filter((row) => urgentSet.has(row.id))
    : attentions;
  return <section id="care-dashboard-attentions" tabIndex="-1" data-testid="cm-attentions" data-visible-count={visible.length} className="scroll-mt-24 outline-none">
    <SectionHeading icon={Bell} title={focus === "urgent" ? t("urgentAttentionTitle") : t("attentionRequired")}
      action={focus === "urgent" ? <button type="button" className="pc-btn pc-btn-ghost" style={{ padding: "6px 10px", fontSize: 11 }} onClick={onClearFocus}>{t("showAll")}</button> : null} />
    {visible.length === 0 ? <div className="pc-card p-3 text-xs" style={{ color: "var(--pc-muted)" }}>{t("noAttentionItems")}</div>
      : <div className="pc-ops-panel divide-y" style={{ borderColor: "var(--pc-border)" }}>{visible.map((attention) => {
        const copy = attentionCopy(attention, t);
        const critical = ["critical", "urgent"].includes(attention.priority);
        return <article key={attention.id} className="relative px-3 py-2.5 grid grid-cols-[minmax(0,1fr)_auto] gap-2.5 items-center"
          data-testid={`attention-${attention.id}`} style={{ borderColor: "var(--pc-border)", borderLeft: critical ? "3px solid var(--pc-score-poor)" : "3px solid transparent" }}>
          <button type="button" className="absolute inset-0 z-0" onClick={() => onOpen(attention)} aria-label={`${t("openSource")}: ${copy.title}`} data-testid={`open-attention-${attention.id}`} />
          <div className="pointer-events-none relative z-10 min-w-0 text-left">
            <div className="flex items-center gap-2 min-w-0"><p className="text-xs font-semibold truncate">{copy.title}</p>{attention.player_id && <span className="text-[10px] shrink-0" style={{ color: "var(--pc-cyan)" }}>{playerById.get(attention.player_id)?.name}</span>}</div>
            {copy.body && <p className="text-[10px] mt-0.5 truncate" style={{ color: "var(--pc-muted)" }}>{copy.body}</p>}
            {attention.source && <p className="text-[9px] mt-1 truncate" style={{ color: "var(--pc-cyan)" }}>{t("source")}: {attention.source.available ? attention.source.title : t("originUnavailable")}</p>}
          </div>
          <div className="relative z-10 flex items-center gap-2 shrink-0">
            <span className="pc-chip" style={{ color: critical ? "var(--pc-score-poor)" : attention.priority === "high" ? "var(--pc-score-medium)" : "var(--pc-muted)", background: critical ? "var(--pc-score-poor-soft)" : "var(--pc-card-inset)" }}>{t(attention.priority)}</span>
            {attention.snooze_allowed && <button type="button" data-testid={`snooze-${attention.id}`} className="pc-btn pc-btn-ghost"
              style={{ padding: "5px 7px", fontSize: 9 }} onClick={() => onSnooze(attention.id)}>{t("snooze24h")}</button>}
            {attention.related_task && <button type="button" data-testid={`attention-task-${attention.id}`} className="pc-btn pc-btn-ghost"
              style={{ padding: "5px 7px", fontSize: 9 }} onClick={() => onOpenTask(attention.related_task.id)}>{t("openTask")}</button>}
            {["critical_health", "injury_reported"].includes(attention.kind) && <><button type="button" className="pc-btn pc-btn-ghost"
              style={{ padding: "6px 9px", fontSize: 10, display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }} onClick={() => onContact(attention)}><MessageSquare size={12} className="shrink-0" />{t("contactPlayer")}</button><button type="button" className="pc-btn pc-btn-ghost"
              style={{ padding: "5px 7px", fontSize: 9 }} onClick={() => onOpenPlayer(attention.player_id)}>{t("openPlayerRecord")}</button></>}
            {attention.kind === "injury_reported" && <button type="button" className="pc-btn pc-btn-primary"
              style={{ padding: "5px 7px", fontSize: 9 }} onClick={() => onActivate(attention)}>{t("assessInjury")}</button>}
          </div>
        </article>;
      })}</div>}
  </section>;
}

function TodayOverview({ rows, onOpen }) {
  const { t } = useI18n();
  return <section data-testid="care-today-overview">
    <div className="flex items-center gap-2 mb-3"><CalendarDays size={18} color="var(--pc-cyan)" /><h2 className="text-lg font-display font-semibold">{t("relevantToday")}</h2></div>
    {rows.length === 0 ? <div className="pc-card p-5 text-sm" style={{ color: "var(--pc-muted)" }}>{t("noRelevantToday")}</div>
      : <div className="pc-card divide-y" style={{ borderColor: "var(--pc-border)" }}>{rows.slice(0, 12).map(({ player, item }) => <button type="button" key={`${player.id}-${item.id}`}
        onClick={() => onOpen(player.id)} className="w-full p-3.5 grid grid-cols-[70px_1fr_auto] gap-3 items-center text-left"
        style={{ background: "transparent", color: "var(--pc-text)", borderColor: "var(--pc-border)" }}>
        <span className="text-xs font-semibold" style={{ color: "var(--pc-cyan)" }}>{compactTime(item.start_at, player.timezone)}</span>
        <span className="min-w-0"><span className="block text-sm font-semibold truncate">{scheduleDisplayTitle(item, t)}</span><span className="block text-xs mt-0.5 truncate" style={{ color: "var(--pc-muted)" }}>{player.name}</span></span>
        <span className="pc-chip" style={{ color: "var(--pc-muted)" }}>{item.training_unit_type ? trainingUnitLabel(item.training_unit_type, t) : t(item.category)}</span>
      </button>)}</div>}
  </section>;
}

const compactTime = (value, timezone) => value ? new Date(value).toLocaleTimeString([], {
  hour: "2-digit", minute: "2-digit", timeZone: timezone || undefined,
}) : "–";

const compactDateTime = (value, timezone, lang) => fmtDateTime(value, timezone, lang);
const compactMatchDate = (value, timezone, lang) => value ? new Intl.DateTimeFormat(lang === "de" ? "de-DE" : "en-GB", {
  day: "2-digit", month: "short", timeZone: timezone || undefined,
}).format(new Date(value)) : "–";
const compactReturnDate = (value, timezone, lang) => value ? new Intl.DateTimeFormat(lang === "de" ? "de-DE" : "en-GB", {
  day: "2-digit", month: "short", timeZone: timezone || undefined,
}).format(new Date(value)) : "–";

function SectionHeading({ icon: Icon, title, subtitle, action }) {
  return <header className="min-h-[36px] mb-2 flex items-center gap-2.5">
    <Icon size={17} color="var(--pc-cyan)" className="shrink-0" />
    <span className="min-w-0"><span className="care-section-title block text-sm font-display font-semibold truncate">{title}</span>{subtitle && <span className="block text-[10px] mt-0.5 truncate" style={{ color: "var(--pc-muted)" }}>{subtitle}</span>}</span>
    {action && <span className="ml-auto shrink-0">{action}</span>}
  </header>;
}

function ModuleShell({ icon, title, subtitle, action, testId, children }) {
  return <section data-testid={testId}>
    <SectionHeading icon={icon} title={title} subtitle={subtitle} action={action} />
    <div className="pc-ops-panel overflow-hidden">{children}</div>
  </section>;
}

const ITEM_ICONS = {
  training: Dumbbell, recovery: HeartPulse, physio: Stethoscope,
  service: Wrench, external_service: Wrench, match: Trophy, game: Trophy,
  task: CheckSquare, care_task: CheckSquare, organization: CalendarRange,
};

const itemStatusColor = (status) => status === "completed" || status === "assumed_completed"
  ? "var(--pc-score-good)" : status === "missed" || status === "overdue"
    ? "var(--pc-score-poor)" : "var(--pc-cyan)";

function LiveTodayModule({ players, rows, onOpen, onOpenCalendar }) {
  const { t } = useI18n();
  const [selectedId, setSelectedId] = useState(players[0]?.player.id || "");
  const [nowMs, setNowMs] = useState(Date.now());
  useEffect(() => {
    if (!players.some((row) => row.player.id === selectedId)) setSelectedId(players[0]?.player.id || "");
  }, [players, selectedId]);
  const selected = players.find((row) => row.player.id === selectedId) || players[0];
  const items = rows.filter(({ player }) => player.id === selected?.player.id);
  const livePlayer = items[0]?.player || selected?.player;
  useEffect(() => {
    const serverMs = Date.parse(livePlayer?.server_time || "");
    const offset = Number.isFinite(serverMs) ? serverMs - Date.now() : 0;
    const tick = () => setNowMs(Date.now() + offset);
    tick(); const timer = window.setInterval(tick, 60000);
    return () => window.clearInterval(timer);
  }, [livePlayer?.server_time]);
  const todayKey = Number.isFinite(nowMs) && livePlayer?.timezone
    ? dayKey(new Date(nowMs).toISOString(), livePlayer.timezone) : null;
  const showNow = Boolean(items.length && livePlayer?.local_date === todayKey);
  const nowIndex = showNow ? items.findIndex(({ item }) => Date.parse(item.start_at || "") > nowMs) : -1;
  const insertAt = showNow ? (nowIndex < 0 ? items.length : nowIndex) : -1;
  const nowLine = <div className="py-1.5 flex items-center gap-2" data-testid="care-live-now-line"><span className="text-[10px] font-semibold uppercase tracking-[.12em]" style={{ color: "var(--pc-cyan)" }}>{t("nowMarker")}</span><span className="h-px flex-1" style={{ background: "linear-gradient(90deg, var(--pc-cyan), transparent)", boxShadow: "0 0 8px var(--pc-cyan-dim)" }} /></div>;
  return <ModuleShell icon={Activity} title={t("liveToday")} subtitle={t("liveTodayHint")} testId="live-today-module">
    <div className="p-3.5">
      {selected && <div className="flex items-center gap-2.5 pb-2.5 mb-0.5 border-b" style={{ borderColor: "var(--pc-border)" }}>
        <Avatar player={selected.player} size={36} /><div className="min-w-0"><p className="text-sm font-semibold truncate">{selected.player.name}</p><p className="text-[10px] mt-0.5" style={{ color: "var(--pc-muted)" }}>{t("localToday")}</p></div>
        <div className="ml-auto flex flex-wrap justify-end gap-2"><button type="button" className="pc-btn pc-btn-ghost" style={{ padding: "7px 11px", fontSize: 12 }} onClick={() => onOpenCalendar(selected.player.id)}>{t("calendar")}</button><button type="button" className="pc-btn pc-btn-primary" data-testid={`open-player-record-${selected.player.id}`} style={{ padding: "7px 11px", fontSize: 12 }} onClick={() => onOpen(selected.player.id)}>{t("openPlayerRecord")}</button>{players.length > 1 && <select className="rounded-lg text-xs px-2 py-1.5 max-w-[132px]" value={selected.player.id}
          aria-label={t("selectPlayer")} onChange={(event) => setSelectedId(event.target.value)} style={{ background: "var(--pc-charcoal-2)", color: "var(--pc-text)", border: "1px solid var(--pc-border)" }}>
          {players.map((row) => <option value={row.player.id} key={row.player.id}>{row.player.name}</option>)}
        </select>}</div>
      </div>}
      {items.length === 0 ? <p className="text-sm py-7 text-center" style={{ color: "var(--pc-muted)" }}>{t("noRelevantToday")}</p>
        : <div>{items.map(({ player, item }, index) => {
          const ItemIcon = ["service", "external_service"].includes(item.category) || item.service_id
            ? getServiceCategoryIcon(item)
            : item.training_unit_type ? getTrainingUnitIcon(item.training_unit_type, ITEM_ICONS[item.category] || Activity)
            : ITEM_ICONS[item.category] || Activity;
          const matchFeedbackOpen = ["match", "game"].includes(item.category) && item.status === "completed" && !item.match_feedback && (item.allowed_actions || []).includes("submit_match_feedback");
          return <Fragment key={item.id}>{index === insertAt && nowLine}<button type="button" onClick={() => onOpenCalendar(player.id)}
             className={`w-full py-2.5 grid grid-cols-[50px_30px_minmax(0,1fr)_auto] gap-2 items-center text-left ${index + 1 === insertAt ? "" : "border-b last:border-b-0"}`} style={{ color: "var(--pc-text)", background: "transparent", borderColor: "var(--pc-border)" }}>
            <span className="text-xs font-semibold" style={{ color: "var(--pc-cyan)" }}>{compactTime(item.start_at, selected?.player.timezone)}</span>
            <span className="w-7 h-7 rounded-lg grid place-items-center" style={{ background: "var(--pc-cyan-dim)", color: "var(--pc-cyan)" }}><ItemIcon size={14} /></span>
            <span className="min-w-0"><span className="block text-xs font-semibold truncate">{scheduleDisplayTitle(item, t)}</span>{item.player_visible_description && item.player_visible_description !== item.title && <span className="block text-[10px] mt-0.5 truncate" style={{ color: "var(--pc-muted)" }}>{item.player_visible_description}</span>}</span>
            <span className="pc-chip" style={{ color: itemStatusColor(item.status), background: "var(--pc-card-inset)" }}>{matchFeedbackOpen ? `${t("ended")} · ${t("feedbackPending")}` : t(item.status)}</span>
          </button></Fragment>;
        })}{insertAt === items.length && nowLine}</div>}
    </div>
  </ModuleShell>;
}

function RequestsModule({ requests, onOpen }) {
  const { t, lang } = useI18n();
  const meaningful = (request) => [...(request.messages || [])].reverse().find((message) =>
    message.visibility !== "care_only" && message.type !== "internal_note" && message.type !== "system"
  );
  return <ModuleShell icon={Mic} title={t("requestsCommunication")} subtitle={t("sharedRequestSystem")} testId="request-module">
    {requests.length === 0 ? <p className="text-xs p-5 text-center" style={{ color: "var(--pc-muted)" }}>{t("noOpenRequests")}</p>
      : <div className="divide-y" style={{ borderColor: "var(--pc-border)" }}>{requests.slice(0, 3).map((request) => {
        const message = meaningful(request);
        const unread = !request.care_read_at && request.status !== "resolved";
        return <button type="button" key={request.id} onClick={() => onOpen(request.id)} data-testid={`dashboard-request-${request.id}`}
          className="w-full p-3 flex gap-2.5 text-left" style={{ background: "transparent", color: "var(--pc-text)", borderColor: "var(--pc-border)" }}>
          <Avatar player={request.player || { name: request.player_id }} size={34} />
          <span className="min-w-0 flex-1"><span className="flex items-center gap-1.5"><span className="text-xs font-semibold truncate">{request.player?.name || request.player_id}</span>{unread && <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: "var(--pc-cyan)", boxShadow: "0 0 8px var(--pc-cyan)" }} aria-label={t("unread")} />}</span>
            <span className="block text-xs font-semibold truncate mt-0.5">{request.subject}</span>
            <span className="block text-[10px] truncate mt-1" style={{ color: "var(--pc-muted)" }}>{message?.text || (message?.audio ? t("voiceNote") : t(request.status))}</span>
          </span>
          <span className="shrink-0 text-right"><span className="block text-[9px] max-w-[74px]" style={{ color: "var(--pc-muted)" }}>{compactDateTime(request.updated_at, request.player?.timezone, lang)}</span><span className="pc-chip mt-1.5" style={{ color: request.status === "new" ? "var(--pc-cyan)" : "var(--pc-score-medium)", background: "var(--pc-card-inset)", fontSize: 9 }}>{t(request.status)}</span>{message?.audio && <span className="mt-1.5 flex justify-end items-center gap-1 text-[10px]" style={{ color: "var(--pc-cyan)" }}><Mic size={10} />{formatDuration(message.audio.duration)}</span>}</span>
        </button>;
      })}</div>}
  </ModuleShell>;
}

export function CurrentTasksModule({ rows, careTasks, onOpen, onOpenTask, onOpenPreparation }) {
  const { t, lang } = useI18n();
  const scheduleTasks = rows.filter(({ item }) => TASK_CATEGORIES.has(item.category));
  const tasks = [
    ...(careTasks || []).map((task) => ({ player: task.player || {}, item: { ...task, category: "care_task" }, careTask: true })),
    ...scheduleTasks.map((task) => ({ ...task, careTask: false })),
  ];
  return <ModuleShell icon={ListChecks} title={t("currentTasks")} subtitle={t("currentTasksHint")} testId="current-tasks-module">
    {tasks.length === 0 ? <p className="text-xs p-5 text-center" style={{ color: "var(--pc-muted)" }}>{t("noCurrentTasks")}</p>
      : <div className="divide-y" style={{ borderColor: "var(--pc-border)" }}>{tasks.slice(0, 3).map(({ player, item, careTask }) => <button type="button" key={item.id} onClick={() => String(item.task_purpose || "").startsWith("onboarding_") ? onOpenPreparation(item.player_id, item.onboarding?.workspace) : careTask ? onOpenTask(item.id) : onOpen(player.id)}
          className="w-full px-3 py-2.5 text-left" style={{ color: "var(--pc-text)", background: "transparent", borderColor: "var(--pc-border)" }} data-testid={`current-task-${item.id}`}>
          <span className="flex items-center gap-2"><span className="text-xs font-semibold truncate flex-1">{item.title}</span><span className="text-[10px] shrink-0" style={{ color: itemStatusColor(item.status) }}>{t(item.status)}</span></span>
          <span className="flex items-center gap-2 mt-1"><span className="text-[10px] truncate flex-1" style={{ color: "var(--pc-muted)" }}>{String(item.task_purpose || "").startsWith("onboarding_") ? `${player.name} · ${t("careStart")}: ${compactCareStartDate(item.onboarding?.care_start_date, lang)}` : player.name?.split(" ")[0]}</span>
          <span className="text-[9px] uppercase font-semibold" style={{ color: item.priority === "high" ? "var(--pc-score-poor)" : "var(--pc-score-medium)" }}>{t(item.priority || "medium")}</span></span>
          {String(item.task_purpose || "").startsWith("onboarding_") && <span className="block text-[10px] mt-1" style={{ color: "var(--pc-cyan)" }}>{item.description}</span>}
        </button>)}</div>
      }
  </ModuleShell>;
}

function DashboardHome({ data, focus, careScope, onNavigate, onOpen, onOpenPreparation, onOpenCalendar, onOpenAttention, onSnooze, onActivate, onOpenTask, onContact, onOpenPlayer, onChanged }) {
  const { t } = useI18n();
  const metrics = data.metrics || {};
  const players = useMemo(() => data.players || [], [data.players]);
  const preparationPlayers = useMemo(() => data.preparation_players || [], [data.preparation_players]);
  const visiblePlayers = useMemo(() => [...players, ...preparationPlayers], [players, preparationPlayers]);
  const playerById = useMemo(() => new Map(visiblePlayers.map((row) => [row.player.id, row.player])), [visiblePlayers]);
  useEffect(() => {
    if (focus !== "urgent") return;
    const target = document.getElementById("care-dashboard-attentions");
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
    target?.focus({ preventScroll: true });
  }, [focus, data.attention_items]);
  return <div className="space-y-4" data-testid="care-dashboard-home">
    <PlayerGroups rows={visiblePlayers} onOpen={onOpen} onOpenPreparation={onOpenPreparation} emptyText={careScope === "mine" ? t("adminMyCareEmpty") : undefined} />
    <section className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3" aria-label={t("workMetrics")}>
      <MetricButton label={t("urgent")} value={metrics.urgent} Icon={AlertTriangle} testId="metric-urgent" onClick={() => onNavigate("dashboard", { focus: "urgent" })} />
      <MetricButton label={t("openRequestsKpi")} value={metrics.open_requests} Icon={ClipboardList} testId="metric-open-requests" onClick={() => onNavigate("requests")} />
      <MetricButton label={t("tasksToday")} value={metrics.tasks_today} Icon={CheckSquare} testId="metric-tasks" onClick={() => onNavigate("tasks")} />
      <MetricButton label={t("stockWarnings")} value={metrics.inventory_warnings} Icon={PackageCheck} testId="metric-stock" onClick={() => onNavigate("inventory")} />
    </section>
    <section className="grid lg:grid-cols-2 gap-3 items-start" aria-label={t("operationalModules")}>
      <div className="space-y-3 min-w-0"><LiveTodayModule players={players} rows={data.today_overview || []} onOpen={onOpen} onOpenCalendar={onOpenCalendar} /><section data-testid="weekly-routine-dashboard-section"><SectionHeading icon={CalendarRange} title={t("weeklyRoutines")} subtitle={t("weeklyRoutineWorkHint")} /><WeeklyRoutineWorkspace legacyRows={data.week_tasks || []} careScope={careScope} onOpenTask={onOpenTask} onChanged={onChanged} /></section></div>
      <div className="space-y-3 min-w-0"><div className="grid xl:grid-cols-2 gap-3 items-start"><RequestsModule requests={data.request_highlights || []} onOpen={(id) => onNavigate("requests", { request: id })} /><CurrentTasksModule rows={data.today_overview || []} careTasks={data.care_tasks || []} onOpen={onOpen} onOpenPreparation={onOpenPreparation} onOpenTask={(id) => onNavigate("tasks", { task: id, bucket: "all" })} /></div><AttentionSection attentions={data.attention_items || []} urgentIds={metrics.urgent_attention_ids || []} focus={focus} onClearFocus={() => onNavigate("dashboard")} onOpen={onOpenAttention} onSnooze={onSnooze} onActivate={onActivate} onOpenTask={onOpenTask} onContact={onContact} onOpenPlayer={onOpenPlayer} playerById={playerById} /></div>
    </section>
  </div>;
}

const DIRECTORY_ICONS = { players: Users, nutrition: Utensils, inventory: PackageCheck };

function DirectoryCardFacts({ row, mode }) {
  const { t, lang } = useI18n();
  const { player, next_match: nextMatch } = row;
  const todayItems = row.today || [];
  const mealItems = todayItems.filter((item) => ["meal", "nutrition"].includes(String(item.category || "").toLowerCase()));
  const supplementItems = todayItems.filter((item) => String(item.category || "").toLowerCase() === "supplement");
  const inventoryWarnings = row.status?.inventory_forecast || [];
  const nutritionAttention = (row.open_attentions || []).find((item) => {
    const key = `${item.kind || ""} ${item.entity_type || ""}`.toLowerCase();
    return key.includes("meal") || key.includes("nutrition") || key.includes("weekly");
  });
  const preparedMeals = mealItems.filter((item) => (item.meal?.meal_kind || item.meal_kind) === "prepared").length;
  const selfCookedMeals = mealItems.filter((item) => (item.meal?.meal_kind || item.meal_kind) === "self_cooked").length;
  const classifiedMeals = preparedMeals + selfCookedMeals;

  if (mode === "nutrition") {
    return <div className="pc-player-entry-card__facts" data-testid="player-card-nutrition-facts">
      <div className="pc-performance-fact pc-performance-fact--wide">
        <span className="pc-performance-fact__icon"><CalendarRange size={15} /></span>
        <span><small>{t("weeklyPlan")}</small><strong>{nutritionAttention ? t("playerReviewPending") : t("planComplete")}</strong></span>
        <span className={`pc-performance-state ${nutritionAttention ? "is-warning" : "is-good"}`}>{nutritionAttention ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}</span>
      </div>
      <div className="pc-performance-fact">
        <span className="pc-performance-fact__icon"><Utensils size={15} /></span>
        <span><small>{t("todayMeals")}</small><strong>{mealItems.length}</strong></span>
      </div>
      <div className="pc-performance-fact">
        <span className="pc-performance-fact__icon"><Activity size={15} /></span>
        <span><small>{t("mealMix")}</small><strong>{classifiedMeals ? `${preparedMeals} / ${selfCookedMeals}` : "–"}</strong></span>
      </div>
      {classifiedMeals > 0 && <div className="pc-performance-ratio" aria-label={`${t("preparedMeals")}: ${preparedMeals}; ${t("mealKind_self_cooked")}: ${selfCookedMeals}`}>
        <span style={{ width: `${Math.round((preparedMeals / classifiedMeals) * 100)}%` }} />
      </div>}
    </div>;
  }

  if (mode === "inventory") {
    const firstWarning = inventoryWarnings[0];
    return <div className="pc-player-entry-card__facts" data-testid="player-card-inventory-facts">
      <div className="pc-performance-fact pc-performance-fact--wide">
        <span className="pc-performance-fact__icon"><PackageCheck size={15} /></span>
        <span><small>{t("supplements")}</small><strong>{inventoryWarnings.length ? t("stockActionRequired") : t("allStocksStable")}</strong></span>
        <span className={`pc-performance-state ${inventoryWarnings.length ? "is-warning" : "is-good"}`}>{inventoryWarnings.length ? <AlertTriangle size={13} /> : <CheckCircle2 size={13} />}</span>
      </div>
      <div className="pc-performance-fact">
        <span className="pc-performance-fact__icon"><AlertTriangle size={15} /></span>
        <span><small>{t("warningsCompact")}</small><strong>{inventoryWarnings.length}</strong></span>
      </div>
      <div className="pc-performance-fact">
        <span className="pc-performance-fact__icon"><ListChecks size={15} /></span>
        <span><small>{t("todayDosesCompact")}</small><strong>{supplementItems.length}</strong></span>
      </div>
      {firstWarning && <p className="pc-performance-note"><AlertTriangle size={12} />{firstWarning.name || t("inventoryWarning")}</p>}
    </div>;
  }

  return <div className="pc-player-entry-card__facts" data-testid="player-card-player-facts">
    <div className="pc-performance-fact pc-performance-fact--wide">
      <span className="pc-performance-fact__icon"><Trophy size={15} /></span>
      <span><small>{t("nextMatch")}</small><strong>{nextMatch?.match?.opponent || t("noUpcomingMatch")}</strong>
        {nextMatch?.start_at && <em>{compactMatchDate(nextMatch.start_at, player.timezone, lang)}</em>}</span>
    </div>
    <div className="pc-performance-fact pc-performance-fact--wide">
      <span className="pc-performance-fact__icon"><ShieldCheck size={15} /></span>
      <span><small>{t("careStatus")}</small><strong>{row.assignment?.relationship === "coverage" ? `${t("coverageFor")} ${row.assignment?.primary_name || t("anotherCareManager")}` : t("primaryPlayer")}</strong></span>
    </div>
  </div>;
}

function PlayerDirectory({ rows, targetTab, title, onOpen, ctaLabel, mode = "players", emptyText }) {
  const nav = useNavigate();
  const { t } = useI18n();
  const DirectoryIcon = DIRECTORY_ICONS[mode] || Users;
  const openPlayer = (player) => onOpen ? onOpen(player) : nav(`/care/players/${player.id}${targetTab ? `?tab=${targetTab}` : ""}`);
  return <section data-testid="care-player-entry-directory">
    <header className={`pc-player-directory-header pc-player-directory-header--${mode}`}>
      <span className="pc-player-directory-header__icon"><DirectoryIcon size={24} /></span>
      <span><p className="text-xs uppercase tracking-[.16em]" style={{ color: "var(--pc-cyan)" }}>Performance Care</p><h2 className="text-2xl font-display font-semibold mt-1">{title}</h2><p className="pc-player-directory-header__hint">{t(`directoryHint_${mode}`)}</p></span>
    </header>
    <div className="pc-player-entry-grid">
      {rows.map((row) => {
        const { player, assignment, primary_metric: metric } = row;
        return <button type="button" key={player.id} onClick={() => openPlayer(player)}
        className={`pc-player-entry-card pc-player-entry-card--${mode} group`} data-testid={`player-record-cta-${player.id}`}>
        <PlayerActionImage player={player} className={`pc-player-action-media--${mode}`} />
        <span className="pc-player-entry-card__eyebrow"><DirectoryIcon size={13} />{t(mode)}</span>
        <div className="pc-player-entry-card__body">
          <div className="pc-player-entry-card__identity min-w-0"><span className="block font-display font-semibold">{player.name}</span><span className="block truncate">{assignment?.relationship === "coverage" ? `${t("coverageFor")} ${assignment?.primary_name || t("anotherCareManager")}` : player.email}</span></div>
          <DirectoryCardFacts row={row} mode={mode} />
          <div className="pc-player-entry-card__footer">
            {mode === "players" && metric && <span className="pc-player-entry-card__metric" style={{ color: "var(--pc-cyan)" }}><span>{t(metric.type)}</span><strong><span className="pc-player-entry-card__metric-value">{Math.round(metric.score || 0)}</span><span className="pc-player-entry-card__metric-percent">%</span></strong></span>}
            <span className={`pc-player-entry-card__cta ml-auto ${mode !== "players" ? "pc-player-entry-card__cta--wide" : ""}`} style={{ color: "var(--pc-text)" }}>{ctaLabel || (targetTab ? t(targetTab) : t("openPlayerRecord"))}<ChevronRight size={14} /></span>
          </div>
        </div>
      </button>;})}
    </div>
    {rows.length === 0 && <div className="pc-card p-8 text-center" style={{ color: "var(--pc-muted)" }}>{emptyText || t("noPlayers")}</div>}
  </section>;
}

function Workspace({ view, data, careScope, theme, setTheme, onCloseNotifications, onChanged }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rows = data.players || [];
  const preparationRows = data.preparation_players || [];
  const playerPath = (id, params = {}) => {
    const query = new URLSearchParams({ scope: careScope, ...params });
    return `/care/players/${id}?${query.toString()}`;
  };
  const scopedParams = (values) => ({ scope: careScope, ...values });
  const emptyText = careScope === "mine" ? t("adminMyCareEmpty") : undefined;
  if (view === "requests") return <CareRequestsPanel careScope={careScope} />;
  if (view === "players") return <PlayerDirectory rows={rows} title={t("players")} mode="players" emptyText={emptyText} onOpen={(player) => nav(playerPath(player.id))} />;
  if (view === "nutrition") return <div className="space-y-5"><PlayerDirectory rows={rows} targetTab="nutrition" title={t("nutrition")} mode="nutrition" emptyText={emptyText} onOpen={(player) => nav(playerPath(player.id, { tab: "nutrition" }))} />{preparationRows.length > 0 && <section data-testid="nutrition-preparation-players"><h2 className="font-display font-semibold mb-2">{t("preparationStatus")}</h2><div className="grid gap-3 max-w-[760px]">{preparationRows.map((row) => <CarePreparationPlayerCard key={row.player.id} row={row} onOpen={(id) => nav(playerPath(id, { tab: "nutrition", selected_date: row.onboarding.care_start_date }))} />)}</div></section>}</div>;
  if (view === "calendar") return <div className="space-y-5">{preparationRows.length > 0 && <section data-testid="calendar-preparation-players"><h2 className="font-display font-semibold mb-2">{t("preparationStatus")}</h2><div className="grid gap-3 max-w-[760px]">{preparationRows.map((row) => <CarePreparationPlayerCard key={row.player.id} row={row} onOpen={(id) => nav(playerPath(id, { tab: "calendar", selected_date: row.onboarding.care_start_date }))} />)}</div></section>}<TodayOverview rows={data.today_overview || []} onOpen={(id) => nav(playerPath(id, { tab: "calendar" }))} /></div>;
  if (view === "tasks") {
    return <CareTasksWorkspace players={rows} careScope={careScope} onChanged={onChanged} />;
  }
  if (view === "inventory") {
    const playerId = searchParams.get("player") || "";
    const itemId = searchParams.get("item") || "";
    if (!playerId && !itemId) return <PlayerDirectory rows={rows} title={t("inventory")} mode="inventory" emptyText={emptyText}
      ctaLabel={t("inventory")} onOpen={(player) => setSearchParams(scopedParams({ view: "inventory", player: player.id }))} />;
    return <CareInventoryWorkspace playerId={playerId} careScope={careScope} onBack={playerId ? () => setSearchParams(scopedParams({ view: "inventory" })) : null} />;
  }
  if (view === "orders") return <CareOrdersWorkspace careScope={careScope} />;
  if (view === "services") return <CareServicesWorkspace careScope={careScope} />;
  if (view === "email") return <CareEmailWorkspace players={rows} />;
  if (view === "reports") return <CareReportsWorkspace players={rows} />;
  if (view === "notifications") return <CareNotificationInbox onNavigate={(path) => nav(path)} onClose={onCloseNotifications} />;
  if (view === "settings") return <section><div className="mb-5"><h2 className="text-xl font-display font-semibold">{t("settings")}</h2><p className="text-sm mt-1" style={{ color: "var(--pc-muted)" }}>{t("careSettingsHint")}</p></div><AccountProfileSettings mode="care" theme={theme} setTheme={setTheme} /><LeaveSelfService /></section>;
  return null;
}

export default function CareManager({ mailWindow = false }) {
  const { t } = useI18n();
  const { user, logout, setUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedScope = searchParams.get("scope");
  const careScope = user?.role === "admin" && requestedScope === "all" ? "all" : "mine";
  const requestedView = mailWindow ? "email" : (searchParams.get("view") || "dashboard");
  const view = VALID_VIEWS.has(requestedView) ? requestedView : "dashboard";
  const [theme, setTheme] = useState(() => normalizeTheme(localStorage.getItem("pc_theme") || user?.theme));
  const [state, setState] = useState("loading");
  const [data, setData] = useState({ players: [], attention_items: [], metrics: {}, today_overview: [], week_tasks: [], request_highlights: [] });
  const [notice, setNotice] = useState("");
  const [contactDraft, setContactDraft] = useState(null);
  const [contactBusy, setContactBusy] = useState(false);
  const mainScrollRef = useRef(null);

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (user?.role === "admin") {
      if (!['mine', 'all'].includes(requestedScope)) next.set("scope", "mine");
    } else if (next.has("scope") && !(user?.role === "care_manager" && requestedScope === "mine")) {
      next.delete("scope");
    }
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [requestedScope, searchParams, setSearchParams, user?.role]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const changeTheme = (next) => {
    setTheme(next);
    api.patch("/me/account", { theme: next }).then(({ data: response }) => {
      setUser((current) => current ? { ...current, ...response.user } : current);
    }).catch(() => {});
  };

  const load = useCallback(async () => {
    setState("loading");
    try {
      const [{ data: dashboard }, { data: requestData }] = await Promise.all([
        api.get("/care/dashboard", { params: { scope: careScope } }),
        api.get("/care/requests", { params: { scope: careScope } }),
      ]);
      setData({
        ...dashboard,
        request_highlights: (requestData.requests || []).filter((request) => request.status !== "resolved").slice(0, 6),
      });
      setState("ready");
    } catch { setState("error"); }
  }, [careScope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [view]);

  const navigateView = (key, extra = {}) => {
    const next = key === "dashboard"
      ? { scope: careScope, ...extra }
      : { scope: careScope, view: key, ...extra };
    setSearchParams(next);
  };
  const changeCareScope = (nextScope) => {
    const next = new URLSearchParams(searchParams);
    next.set("scope", nextScope);
    for (const key of ["player", "item", "order", "task", "request", "service"]) next.delete(key);
    setSearchParams(next);
  };
  const withCareScope = useCallback((path) => {
    if (typeof path !== "string" || !path.startsWith("/care")) return path;
    const parsed = new URL(path, "http://performance-care.local");
    parsed.searchParams.set("scope", careScope);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  }, [careScope]);
  const closeNotifications = useCallback(() => {
    const origin = location.state?.notificationOrigin;
    if (typeof origin === "string" && origin.startsWith("/care")
        && !origin.includes("view=notifications")) {
      nav(withCareScope(origin), { replace: true, state: null });
      return;
    }
    setSearchParams({ scope: careScope }, { replace: true });
  }, [careScope, location.state, nav, setSearchParams, withCareScope]);
  const snooze = async (id) => {
    try {
      await api.post(`/care/attentions/${id}/snooze`, { until: new Date(Date.now() + 86400000).toISOString() });
      setNotice(t("attentionSnoozed")); await load();
    } catch { setNotice(t("attentionCannotSnooze")); }
  };
  const assessInjury = (attention) => {
    const params = new URLSearchParams({ tab: "recovery" });
    if (attention.entity_id) params.set("injury", attention.entity_id);
    nav(withCareScope(`/care/players/${attention.player_id}?${params.toString()}`));
  };
  const contactFromAttention = async (attention) => {
    try {
      const { data: response } = await api.post(`/care/attentions/${attention.id}/contact-player`);
      if (response.request) {
        nav(withCareScope(`/care?view=requests&request=${response.request.id}`), { state: {
          requestDraft: response.draft_message, requestDraftFor: response.request.id,
          focusRequestComposer: true,
        } });
        return;
      }
      setContactDraft({
        attentionId: attention.id, player: response.player,
        message: response.draft_message,
        idempotencyKey: globalThis.crypto?.randomUUID?.() || `${attention.id}-${Date.now()}`,
      });
    } catch { setNotice(t("contactPlayerFailed")); }
  };
  useEffect(() => {
    const prepared = location.state?.preparedAttentionContact;
    if (!prepared) return;
    if (prepared.request) {
      nav(withCareScope(`/care?view=requests&request=${prepared.request.id}`), { replace: true, state: {
        requestDraft: prepared.draft_message, requestDraftFor: prepared.request.id,
        focusRequestComposer: true,
      } });
      return;
    }
    setContactDraft({
      attentionId: prepared.attention_id, player: prepared.player,
      message: prepared.draft_message,
      idempotencyKey: globalThis.crypto?.randomUUID?.() || `${prepared.attention_id}-${Date.now()}`,
    });
    nav(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, nav, withCareScope]);
  const sendContactDraft = async () => {
    if (!contactDraft?.message.trim()) return;
    setContactBusy(true);
    try {
      const { data: response } = await api.post(
        `/care/attentions/${contactDraft.attentionId}/contact-player/send`,
        { message: contactDraft.message.trim(), idempotency_key: contactDraft.idempotencyKey },
      );
      setContactDraft(null);
      nav(withCareScope(`/care?view=requests&request=${response.request.id}`));
    } catch { setNotice(t("contactPlayerFailed")); }
    finally { setContactBusy(false); }
  };
  const openDashboardAttention = (attention) => {
    if (attention.deep_link) return nav(withCareScope(attention.deep_link));
    if (attention.entity_type === "care_request") {
      return nav(withCareScope(`/care?view=requests&request=${attention.entity_id}`));
    }
    if (attention.entity_type === "service" || String(attention.kind || "").includes("service")) {
      return nav(withCareScope(`/care?view=services${attention.entity_id ? `&service=${attention.entity_id}` : ""}`));
    }
    if (attention.entity_type === "meal_order") {
      return nav(withCareScope(`/care?view=orders&order=${attention.entity_id}`));
    }
    if (attention.entity_type === "inventory_item" || String(attention.kind || "").includes("inventory")) {
      return nav(withCareScope(`/care?view=inventory${attention.entity_id ? `&item=${attention.entity_id}` : ""}`));
    }
    const row = (data.players || []).find((candidate) => candidate.player.id === attention.player_id);
    if (attention.entity_type === "injury_case" || attention.entity_type === "training_feedback"
        || String(attention.kind || "").includes("injury")) {
      return nav(withCareScope(`/care/players/${attention.player_id}?tab=overview&attention=${attention.id}`));
    }
    if (attention.entity_type === "today_item" || String(attention.kind || "").includes("task")) {
      const scheduleRow = (data.today_overview || []).find(({ item }) => item.id === attention.entity_id);
      const itemDate = scheduleRow?.item?.start_at
        ? dayKey(scheduleRow.item.start_at, scheduleRow.player.timezone) : "";
      const query = new URLSearchParams({ tab: "calendar" });
      if (itemDate) query.set("selected_date", itemDate);
      return nav(withCareScope(`/care/players/${attention.player_id}?${query.toString()}`));
    }
    if (String(attention.kind || "").includes("coverage") && user?.role === "admin") {
      return nav("/admin?tab=assignments");
    }
    const params = new URLSearchParams({ tab: "overview" });
    if (attention.entity_type === "daily_summary" || attention.kind === "low_daily_score") {
      const summaryDate = row?.last_daily_summary?.local_date;
      if (summaryDate) params.set("selected_date", summaryDate);
    }
    nav(withCareScope(`/care/players/${attention.player_id}?${params.toString()}`));
  };

  const title = t(view === "notifications" ? "notifications" : view === "email" ? "email" : (NAV_ITEMS.find(([key]) => key === view)?.[1] || "dashboard"));
  if (mailWindow) return <div className="pc-mail-window min-h-screen" data-testid="care-mail-window">
    <header className="pc-mail-window__header">
      <div className="flex items-center gap-3"><span className="pc-mail-window__icon"><Mail size={20} /></span><div><p>{t("appName")}</p><h1>{t("email")}</h1></div></div>
      <ThemeLevelSlider value={theme} onChange={changeTheme} testId="mail-window-theme-slider" compact />
    </header>
    <main className="pc-mail-window__main">
      {state === "loading" && <div className="flex justify-center py-24" data-testid="cm-loading"><div className="pc-spinner" /></div>}
      {state === "error" && <div className="pc-card p-6" data-testid="cm-error"><p style={{ color: "#ff8080" }}>{t("todayError")}</p><button onClick={load} className="pc-btn pc-btn-ghost mt-4">{t("retry")}</button></div>}
      {state === "ready" && <CareEmailWorkspace players={data.players || []} />}
    </main>
  </div>;
  return <div className={`care-shell flex${view === "calendar" ? " care-calendar-shell" : ""}`}>
    <CareSidebar view={view} onNavigate={navigateView} theme={theme} setTheme={changeTheme} user={user} logout={logout} />
    <div className="pc-care-main-scroll min-w-0 flex-1" ref={mainScrollRef} data-testid="care-main-scroll">
      <header className={`${view === "dashboard" ? "h-[72px]" : "h-[88px]"} border-b px-6 xl:px-9 flex items-center justify-between sticky top-0 z-20`}
        style={{ background: "color-mix(in srgb, var(--pc-black) 88%, transparent)", borderColor: "var(--pc-border)", backdropFilter: "blur(16px)" }}>
        <div className="flex items-center gap-5 min-w-0"><div className="min-w-0"><p className="text-xs uppercase tracking-[.16em] truncate" style={{ color: user?.role === "admin" ? "var(--pc-cyan)" : "var(--pc-muted)" }}>{user?.role === "admin" ? t("adminCareOperations") : t("careManager")}</p><h1 className="text-2xl font-display font-bold mt-1 truncate">{title}</h1></div>{user?.role === "admin" && <CareScopeToggle scope={careScope} onChange={changeCareScope} />}</div>
        <div className="flex items-center gap-2"><PlayerNotificationBell compactDesktop inboxPath={withCareScope("/care?view=notifications")} onClick={view === "notifications" ? closeNotifications : null} /><button type="button" onClick={load} className="pc-btn pc-btn-ghost flex items-center gap-2" style={{ padding: "9px 13px", fontSize: 13 }}><RefreshCw size={15} /> {t("refresh")}</button></div>
      </header>
      <main className={`w-full max-w-none px-6 xl:px-9 ${view === "dashboard" ? "py-4" : "py-8"}`} data-testid="care-main">
        {notice && <div className="pc-card p-3 mb-5 text-sm" role="status" style={{ color: "var(--pc-cyan)" }}>{notice}</div>}
        {state === "loading" && <div className="flex justify-center py-24" data-testid="cm-loading"><div className="pc-spinner" /></div>}
        {state === "error" && <div className="pc-card p-6" data-testid="cm-error"><p style={{ color: "#ff8080" }}>{t("todayError")}</p><button onClick={load} className="pc-btn pc-btn-ghost mt-4">{t("retry")}</button></div>}
        {state === "ready" && (view === "dashboard" ? <DashboardHome data={data} focus={searchParams.get("focus")} careScope={careScope}
          onNavigate={navigateView} onOpen={(id) => nav(withCareScope(`/care/players/${id}`))}
          onOpenPreparation={(id, workspace) => {
            const row = (data.preparation_players || []).find((candidate) => candidate.player.id === id);
            const query = new URLSearchParams({ tab: workspace || "overview" });
            if (row?.onboarding?.care_start_date && workspace) query.set("selected_date", row.onboarding.care_start_date);
            nav(withCareScope(`/care/players/${id}?${query.toString()}`));
          }}
          onOpenCalendar={(id) => nav(withCareScope(`/care/players/${id}?tab=calendar`))} onOpenAttention={openDashboardAttention} onSnooze={snooze} onActivate={assessInjury}
          onOpenTask={(id) => nav(withCareScope(careTaskDeepLink(id)))}
          onContact={contactFromAttention} onOpenPlayer={(id) => nav(withCareScope(`/care/players/${id}`))} onChanged={load} />
          : <Workspace view={view} data={data} careScope={careScope} theme={theme} setTheme={changeTheme} onCloseNotifications={closeNotifications} onChanged={load} />)}
      </main>
      {contactDraft && <div className="fixed inset-0 z-[80] grid place-items-center p-5" style={{ background: "rgba(0,0,0,.76)", backdropFilter: "blur(8px)" }} data-testid="attention-contact-composer">
        <section className="pc-card w-full max-w-lg p-5">
          <p className="text-xs uppercase tracking-[.14em]" style={{ color: "var(--pc-cyan)" }}>{t("attentionContactTitle")}</p>
          <h2 className="text-xl font-display font-semibold mt-1">{contactDraft.player?.name}</h2>
          <p className="text-xs mt-2" style={{ color: "var(--pc-muted)" }}>{t("attentionContactHint")}</p>
          <textarea autoFocus className="pc-input mt-4" rows="6" value={contactDraft.message}
            onChange={(event) => setContactDraft((current) => ({ ...current, message: event.target.value }))} data-testid="attention-contact-draft" />
          <div className="flex justify-end gap-2 mt-4">
            <button type="button" className="pc-btn pc-btn-ghost" disabled={contactBusy} onClick={() => setContactDraft(null)}>{t("cancel")}</button>
            <button type="button" className="pc-btn pc-btn-primary flex items-center gap-2" disabled={contactBusy || !contactDraft.message.trim()} onClick={sendContactDraft}><MessageSquare size={14} />{t("send")}</button>
          </div>
        </section>
      </div>}
    </div>
  </div>;
}
